/**
 * CampaignSequenceEntryDetailDialog
 * 
 * IMPORTANT: Dialog closing pattern for CrudPage nested dialogs
 * 
 * This dialog follows a specific pattern to avoid "Node.removeChild" errors:
 * 1. Use `visible={true}` - Dialog is always visible when rendered (CrudPage controls rendering via dialogOpen)
 * 2. `handleSave` only calls `onSave` - does NOT close the dialog itself
 * 3. `handleClose` only calls `onClose` - does NOT close the dialog itself
 * 4. CrudPage handles dialog closing via `setDialogOpen(false)` with a 150ms delay
 * 
 * This pattern prevents race conditions where the dialog tries to close itself
 * while CrudPage is unmounting the component, causing DOM manipulation errors.
 * 
 * Reference: Similar pattern used in OperationDetailDialog.tsx
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import type { CampaignSequenceEntryDto } from '../../api/models/CampaignSequenceEntryDto';
import type { MaterialDto } from '../../api/models/MaterialDto';
import type { CrudFormProps } from '../../components/CrudPage';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import { generateTempUUID } from '../../utils/uuid';

type CampaignSequenceEntryWithId = CampaignSequenceEntryDto & { id: string };

interface CampaignSequenceEntryDetailDialogProps extends CrudFormProps<CampaignSequenceEntryWithId> {
  allEntries?: CampaignSequenceEntryDto[]; // Optional: all entries to check for multiple occurrences
  canSetMaxBatches?: boolean; // Optional: whether maxBatches can be set (if product occurs multiple times)
}

export const CampaignSequenceEntryDetailDialog: React.FC<CampaignSequenceEntryDetailDialogProps> = ({
  value,
  onClose,
  onSave,
  allEntries,
  canSetMaxBatches
}) => {
  const { t } = useTranslation(['common']);
  const [products, setProducts] = useState<MaterialDto[]>([]);
  const [loading, setLoading] = useState(false);

  const schema = z.object({
    productId: z.string().min(1, t('validation.required')),
    maxBatches: z.number().optional()
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      productId: value?.productId || '',
      maxBatches: value?.maxBatches ?? undefined
    }
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const materials = await MaterialControllerService.findAll();
      // Main products only - alias products must not appear in the sequence
      const productList = materials.filter(
        m => m.materialType === 'PRODUCT' && m.isAlias !== true
      );
      setProducts(productList);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = handleSubmit(async (formData) => {
    const selectedProduct = products.find(p => p.uuid === formData.productId);
    
    const dataToSave: CampaignSequenceEntryWithId = {
      ...value,
      ...formData,
      productName: selectedProduct?.name || '',
      uuid: value?.uuid || generateTempUUID(),
      id: value?.id || value?.uuid || generateTempUUID()
    };

    // Call onSave - CrudPage will handle closing the dialog
    // Don't close the dialog here to avoid race conditions
    await Promise.resolve(onSave(dataToSave));
  });

  const handleClose = () => {
    // Don't set visible to false here - let CrudPage handle it via onClose
    // Just call onClose which will trigger CrudPage's handleDialogClose
    onClose();
  };

  const productOptions = products
    .filter(p => p.uuid && p.name)
    .map(p => ({
      value: p.uuid as string,
      text: p.name as string
    }));

  const footerTemplate = () => (
    <div className="flex justify-end gap-2 p-2">
      <ButtonComponent 
        onClick={handleClose} 
        cssClass="e-outline e-btn e-outline"
        type="button"
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent 
        onClick={handleSave} 
        cssClass="e-primary e-btn e-primary"
        type="button"
        disabled={isSubmitting}
      >
        {isSubmitting ? t('actions.saving') : t('actions.ok')}
      </ButtonComponent>
    </div>
  );

  return (
    <DialogComponent
      width="600px"
      isModal={true}
      visible={true}
      close={handleClose}
      header={
        value?.uuid 
          ? t('campaignSequenceEntry.form.editTitle') 
          : t('campaignSequenceEntry.form.createTitle')
      }
      showCloseIcon={true}
      cssClass="campaign-sequence-entry-dialog"
      footerTemplate={footerTemplate}
    >
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="productId">{t('campaignSequenceEntry.form.product')}*</label>
          <Controller
            name="productId"
            control={control}
            render={({ field }) => (
              <DropDownListComponent
                id="productId"
                dataSource={productOptions}
                fields={{ text: 'text', value: 'value' }}
                value={field.value}
                change={(e: any) => field.onChange(e.value)}
                placeholder={t('campaignSequenceEntry.form.productPlaceholder')}
                floatLabelType="Never"
                enabled={!loading}
              />
            )}
          />
          {errors.productId && (
            <span style={{ color: 'red', fontSize: '12px' }}>
              {errors.productId.message}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="maxBatches">{t('campaignSequenceEntry.form.maxBatches')}</label>
          <Controller
            name="maxBatches"
            control={control}
            render={({ field }) => {
              // Determine if maxBatches can be set
              // If canSetMaxBatches prop is provided, use it
              // Otherwise, check if product occurs multiple times in allEntries
              let canSet = canSetMaxBatches;
              if (canSet === undefined && allEntries && value?.productId) {
                const occurrences = allEntries.filter(e => e.productId === value.productId).length;
                canSet = occurrences > 1;
              }
              
              return (
                <NumericTextBoxComponent
                  id="maxBatches"
                  value={field.value ?? undefined}
                  change={(e: any) => {
                    const newValue = e.value === null || e.value === undefined || e.value === '' 
                      ? undefined
                      : e.value;
                    field.onChange(newValue);
                  }}
                  placeholder={t('campaignSequenceEntry.form.maxBatchesPlaceholder')}
                  floatLabelType="Never"
                  format="N0"
                  min={1}
                  step={1}
                  enabled={canSet !== false} // Enable only if product occurs multiple times
                />
              );
            }}
          />
          {errors.maxBatches && (
            <span style={{ color: 'red', fontSize: '12px', display: 'block' }}>
              {errors.maxBatches.message}
            </span>
          )}
        </div>
      </div>
    </DialogComponent>
  );
};
